/**
 * ShopMed24 — C:Hub (Customer Hub by Froonze) brand styling
 * Created: 2026-05-10
 * Updated: 2026-05-21 — Forgot Password modal blur fix (z-index 2147483647 +
 *   isolation:isolate + ::before backdrop + broader drawer-hide selector
 *   covering accounts-everywhere/hub/customer-hub web components).
 * Updated: 2026-05-21 (later) — German copy polish: Denglish "Reset-Link" →
 *   "Link zum Zurücksetzen", error body wording. English title-case.
 * Updated: 2026-05-21 (later 4) — Forgot Password now does a top-level
 *   form submit to /account/recover (no fetch, no iframe). CF challenges
 *   ALL POSTs to that endpoint regardless of submit mode, and the
 *   Storefront API GraphQL approach needs `unauthenticated_write_customers`
 *   scope (custom-app + token). A click-driven full-page form submit is
 *   the only path CF reliably lets through. The drawer at
 *   /account/login?customer_recover=true renders a branded success banner
 *   (sections/sm24-customer-login.liquid).
 * Updated: 2026-05-22 — Mobile-only fixes for C:Hub signup form.
 *   Added @media (max-width: 640px) block at the end of BRAND_CSS:
 *   buttons full-width 48px, inputs stacked + 16px font (no iOS zoom),
 *   form rows vertical, post-signup welcome buttons full-width.
 *   Tablet + desktop unchanged.
 * Updated: 2026-05-22 (later) — Desktop C:Hub sidebar nav labels fix.
 *   [class*="active"] was a substring match that ALSO matched "inactive",
 *   forcing white-text-on-white-bg on every non-selected sidebar item =
 *   invisible labels. Replaced with whole-word [class~="..."] + named
 *   classes (.active, .is-active, .selected, .current). Affects desktop +
 *   mobile equally — pure rule tightening, no scope change.
 * Updated: 2026-05-22 (later 2) — Customer Hub sidebar labels still blank.
 *   Root cause: C:Hub uses `color: var(--ae-menu-text, var(--ae-bg-contrast))`
 *   and our brand CSS never defined --ae-menu-text, --ae-menu-bg,
 *   --ae-menu-active-bg, --ae-menu-active-text, --ae-bg-secondary, or
 *   --ae-bg-contrast. Fallback resolved to a color that matched the
 *   rendered background = invisible labels. Defined all of them + added
 *   high-specificity `.menu #menuItems .inner-menu a` override.
 * Updated: 2026-05-22 (later 3) — The CSS fix above was a partial diagnosis.
 *   DOM inspect via chrome-mcp shows each <a id="ae-menu-N"> contains
 *   only <span></span> — the C:Hub widget renders the layout but never
 *   populates the label text (missing app-config string / locale bundle).
 *   Added populateHubMenuLabels() that backfills the empty spans based
 *   on each <a>'s href (#account:a:dashboard → Übersicht, etc.), in
 *   the current locale. Runs on init + MutationObserver + retries via
 *   applyAll(). Now labels render correctly on /a/customerhub.
 * Updated: 2026-05-22 (later 4) — Mobile signup screen polish.
 *   (a) Hide .menu (dashboard tabs) on signup/login overlays via
 *       :has() — C:Hub\'s own rule failed because .menu is a sibling of
 *       .widget--registration, not a child.
 *   (b) Force .b-gr containers to width:100% on ≤640px — was the root
 *       cause of the Registrieren button rendering at 266px in a 354px
 *       viewport.
 *   (c) Style .register-link ("Bereits angemeldet? Hier einloggen")
 *       as a full-width outlined button on mobile.
 *
 * C:Hub renders <accounts-everywhere> as a Web Component with open Shadow DOM.
 * This script injects ShopMed24 brand styles into the shadow root so the widget
 * matches our site (#8db9ca / #4b7381 / Inter font / 12px radius).
 *
 * Re-applies on widget mount and re-renders via MutationObserver.
 */
(function () {
  'use strict';

  var STYLE_ID = 'sm24-chub-brand-styles';

  // Brand-matching CSS — overrides C:Hub's default black/white via CSS vars + direct selectors
  var BRAND_CSS = [
    /* Brand variable overrides — propagate to all shadow elements via universal selector */
    '* {',
    '  --ae-btn-secondary-bg: #4b7381 !important;',
    '  --ae-btn-secondary-text: #ffffff !important;',
    '  --ae-btn-primary-bg: #8db9ca !important;',
    '  --ae-btn-primary-text: #4b7381 !important;',
    '  --ae-primary-color: #4b7381 !important;',
    '  --ae-text-highlight: #4b7381 !important;',
    '  --ae-text-primary: #1f2e35 !important;',
    '  --ae-text-secondary: #45474d !important;',
    '  --ae-text-muted: #6b7280 !important;',
    '  --ae-border-color: rgba(141,185,202, 0.35) !important;',
    '  --ae-content-radius: 14px !important;',
    '  --ae-bg-content: #ffffff !important;',
    '  --ae-bg-body: #f4f8fa !important;',
    '  --ae-bg-highlight: #f4f8fa !important;',
    '  --ae-bg-secondary: #ffffff !important;',
    '  --ae-bg-contrast: #4b7381 !important;',
    '  --ae-button-bg: #4b7381 !important;',
    '  --ae-shadow-default: 0 4px 12px rgba(75,115,129, 0.12) !important;',
    '  --ae-shadow-light: 0 1px 3px rgba(15, 23, 42, 0.06) !important;',
    /* C:Hub Customer Hub sidebar menu — these were UNDEFINED, causing the */
    /* `color: var(--ae-menu-text, var(--ae-bg-contrast))` fallback to */
    /* resolve to whatever made text invisible on the rendered bg. */
    '  --ae-menu-bg: #ffffff !important;',
    '  --ae-menu-text: #1f2e35 !important;',
    '  --ae-menu-active-bg: #4b7381 !important;',
    '  --ae-menu-active-text: #ffffff !important;',
    '  --ae-menu-group-bg: transparent !important;',
    '  --ae-menu-item-border: solid 1px rgba(141,185,202, 0.25) !important;',
    '  --ae-menu-item-gap: 8px !important;',
    '}',
    /* ════════════════════════════════════════════════════════════════
       2026-05-23: HIDE SIDEBAR/TABS ON SIGNUP + SIGN-IN PAGES (ALL VIEWPORTS).
       Previously the .menu hide rule was ONLY in a @media (max-width: 640px)
       block — so on desktop the Profil/Übersicht/Bestellungen/Abmelden tabs
       were leaking into the signup/login overlays.
       Targets ONLY the .menu container (sidebar nav). Do NOT use visibility/
       width/height rules — they break the drawer layout. Just display:none.
       See: chub_auth_LOCKED_2026_05_23.md + chub_sidebar_labels_2026_05_22.md
       ════════════════════════════════════════════════════════════════ */
    '.widget--registration .menu,',
    '.widget--login .menu,',
    '.widget--reset .menu,',
    '.widget--recover .menu {',
    '  display: none !important;',
    '}',
    /* ════════════════════════════════════════════════════════════════
       2026-05-26: C:Hub UPDATE REGRESSION — Froonze removed the
       widget--registration / widget--login / widget--reset / widget--recover
       modifier classes. Now ALL overlay screens (signup, login, reset,
       recover) just have plain class "widget open". Our old selectors above
       still load but no longer match. Re-using the more robust signal:
       #ae-holder has attribute `isloggedin="false"` on all logged-out
       screens (register/login/reset/recover) and `isloggedin="true"` on the
       dashboard. Hiding the menu when isloggedin="false" is semantically
       correct: the menu is dashboard navigation, only relevant once logged in.
       Keep the old rules above for backwards compat if Froonze restores
       modifier classes in a future update.
       ════════════════════════════════════════════════════════════════ */
    '#ae-holder[isloggedin="false"] .menu,',
    'accounts-everywhere #ae-holder[isloggedin="false"] .menu,',
    'accounts-hub #ae-holder[isloggedin="false"] .menu {',
    '  display: none !important;',
    '}',
    /* High-specificity override for the C:Hub Customer Hub left sidebar.
       Targets `.menu #menuItems .inner-menu a` (and its child span) exactly
       so our colors win regardless of which CSS-variable fallback fires. */
    '.menu #menuItems .inner-menu a {',
    '  background: #ffffff !important;',
    '  color: #1f2e35 !important;',
    '  border-color: rgba(141,185,202, 0.35) !important;',
    '}',
    '.menu #menuItems .inner-menu a.active,',
    '.menu #menuItems .inner-menu a.is-active,',
    '.menu #menuItems .inner-menu a[aria-current="page"],',
    '.menu #menuItems .inner-menu a[aria-current="true"] {',
    '  background: #6ba4b8 !important;',
    '  color: #ffffff !important;',
    '}',
    /* Force descendants (spans, icons) to inherit color so labels render. */
    '.menu #menuItems .inner-menu a > *,',
    '.menu #menuItems .inner-menu a span {',
    '  color: inherit !important;',
    '  fill: currentColor !important;',
    '  font-size: 15px !important;',
    '  line-height: 1.4 !important;',
    '}',
    /* Direct selector overrides — high specificity to beat C:Hub internal rules */
    '.b-gr button.btn-secondary, .b-gr a.btn-a.btn-secondary, button.btn-secondary, .btn-secondary {',
    '  background: #6ba4b8 !important;',
    '  background-color: #6ba4b8 !important;',
    '  color: #ffffff !important;',
    '  border: none !important;',
    '  border-radius: 12px !important;',
    '  font-weight: 600 !important;',
    '  padding: 12px 24px !important;',
    '  transition: all .2s ease !important;',
    '  cursor: pointer !important;',
    '  letter-spacing: 0.01em !important;',
    '  box-shadow: 0 2px 4px rgba(75,115,129, 0.15) !important;',
    '}',
    '.b-gr button.btn-secondary:hover, .b-gr a.btn-a.btn-secondary:hover, button.btn-secondary:hover, .btn-secondary:hover {',
    '  background: #5a93a8 !important;',
    '  background-color: #5a93a8 !important;',
    '  box-shadow: 0 4px 12px rgba(75,115,129, 0.30) !important;',
    '  transform: translateY(-1px) !important;',
    '}',
    '.b-gr button.btn-primary, button.btn-primary, .btn-primary {',
    '  background: #6ba4b8 !important;',
    '  background-color: #6ba4b8 !important;',
    '  color: #ffffff !important;',
    '  border-radius: 12px !important;',
    '  font-weight: 600 !important;',
    '}',
    /* Headings */
    '.heading-md, .c-hd, .c-inr h1, .c-inr h2, .c-inr h3 {',
    '  color: #4b7381 !important;',
    '  font-weight: 700 !important;',
    '  letter-spacing: -0.01em !important;',
    '}',
    /* Tabs (Homebase / Orders / Profile menu) */
    '.menu, .inner-menu { background: #ffffff !important; }',
    '.menu .active, .inner-menu .active {',
    '  color: #4b7381 !important;',
    '  border-bottom-color: #8db9ca !important;',
    '  font-weight: 700 !important;',
    '}',
    /* Universal: typography */
    '* {',
    '  font-family: \'Inter\', -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif !important;',
    '}',
    /* Container card */
    '.c-bx, .b-gr {',
    '  border-color: rgba(141,185,202, 0.35) !important;',
    '  border-radius: 14px !important;',
    '}',
    /* Body text */
    '.body-md, .body-bold {',
    '  color: #45474d !important;',
    '  line-height: 1.6 !important;',
    '}',
    /* Links */
    '.register-link a, .forgot-password a, a.link {',
    '  color: #5b8b9c !important;',
    '  text-decoration: none !important;',
    '  font-weight: 600 !important;',
    '}',
    '.register-link a:hover, .forgot-password a:hover, a.link:hover {',
    '  color: #4b7381 !important;',
    '  text-decoration: underline !important;',
    '}',
    /* Inputs */
    'input, select, textarea {',
    '  border-color: #d1d5db !important;',
    '  border-radius: 10px !important;',
    '  font-family: \'Inter\', sans-serif !important;',
    '}',
    'input:focus, select:focus, textarea:focus {',
    '  border-color: #8db9ca !important;',
    '  outline: 2px solid rgba(141,185,202, 0.25) !important;',
    '  outline-offset: 0 !important;',
    '}',
    /* ────────────────────────────────────────────────────────────
       SM24 2026-05-12: Hub Page menu item visibility WIDE NET.
       We don't know C:Hub's exact class names inside <accounts-hub>
       shadow DOM, so target a very wide range of nav-related selectors.
       ──────────────────────────────────────────────────────────── */
    /* Wide net: any list/link/button inside any container that smells like nav */
    'nav a, nav li, nav button, nav span,',
    'aside a, aside li, aside button, aside span,',
    '[role="navigation"] a, [role="navigation"] li, [role="navigation"] button,',
    '.menu a, .menu li, .menu button, .menu span,',
    '.inner-menu a, .inner-menu li, .inner-menu button, .inner-menu span,',
    '[class*="menu"] a, [class*="menu"] li, [class*="menu"] button, [class*="menu"] span,',
    '[class*="nav"] a, [class*="nav"] li, [class*="nav"] button, [class*="nav"] span,',
    '[class*="sidebar"] a, [class*="sidebar"] li, [class*="sidebar"] button, [class*="sidebar"] span,',
    '[class*="tab"] a, [class*="tab"] li, [class*="tab"] button, [class*="tab"] span {',
    '  font-size: 14px !important;',
    '  line-height: 1.4 !important;',
    '  visibility: visible !important;',
    '  opacity: 1 !important;',
    '  font-family: Inter, -apple-system, sans-serif !important;',
    '}',
    /* Default text color — dark navy — for any nav item NOT marked active.
       NOTE 2026-05-22: previously used [class*="active"] which is a substring
       match — it ALSO matched "inactive", which made non-selected sidebar
       items get white-text-on-white-bg (invisible labels). Switched to
       whole-word [class~="..."] + explicit class names so only real
       active-state classes match. */
    'nav a:not([aria-current]):not(.active):not(.is-active):not(.selected):not(.current),',
    'aside a:not([aria-current]):not(.active):not(.is-active):not(.selected):not(.current),',
    '.menu a:not(.active):not(.is-active):not(.selected):not(.current),',
    '.inner-menu a:not(.active):not(.is-active):not(.selected):not(.current),',
    '[class*="menu"] a:not(.active):not(.is-active):not(.selected):not(.current):not([aria-current]),',
    '[class*="nav"] a:not(.active):not(.is-active):not(.selected):not(.current):not([aria-current]) {',
    '  color: #1f2e35 !important;',
    '}',
    /* Active state — white text on dark pill. Whole-word match only so
       "inactive" / "deactivated" / etc. do NOT match. */
    '[aria-current="page"], [aria-current="true"], [aria-selected="true"],',
    '.active, .is-active, .selected, .current,',
    '[class~="active"], [class~="is-active"], [class~="selected"] {',
    '  color: #ffffff !important;',
    '}',
    '[aria-current="page"] *, [aria-current="true"] *, [aria-selected="true"] *,',
    '.active *, .is-active *, .selected *, .current *,',
    '[class~="active"] *, [class~="is-active"] *, [class~="selected"] * {',
    '  color: #ffffff !important;',
    '  fill: #ffffff !important;',
    '}',
    '/* Icon font carve-out — keep C:Hub icon glyphs in their own font, not Inter */',
    '[class^="ch-icon-"], [class*=" ch-icon-"], [class^="ae-icon-"], [class*=" ae-icon-"], i.ch, i.ae {',
    '  font-family: \'ch-icons\', \'ae-icons\', \'Material Symbols Outlined\', sans-serif !important;',
    '  speak: none;',
    '  font-style: normal !important;',
    '  font-weight: normal !important;',
    '  font-variant: normal !important;',
    '  text-transform: none !important;',
    '  line-height: 1 !important;',
    '  -webkit-font-smoothing: antialiased !important;',
    '  -moz-osx-font-smoothing: grayscale !important;',
    '}',
    /* ────────────────────────────────────────────────────────────
       SM24 2026-05-22: Mobile-only fixes for C:Hub signup screen.
       Sign In drawer is OK; only signup (registration) + post-signup
       welcome buttons need help on small viewports. Desktop and
       tablet (>=641px) are UNTOUCHED — these rules are inside a
       max-width media query.
       ──────────────────────────────────────────────────────────── */
    '@media (max-width: 640px) {',
    /* ────────────────────────────────────────────────────────────
       2026-05-22 (later 4): Signup screen specifics.
       (a) When .widget--registration is in the DOM, hide the dashboard
           .menu tabs at top — C:Hub\'s own rule `.widget--registration .menu`
           never matches because .menu is a SIBLING of .widget--registration,
           not a child. :has() works in modern browsers (Chrome 105+,
           Safari 15.4+, Firefox 121+) which covers all current phones.
       (b) Force .b-gr containers AND their buttons to full width so the
           Registrieren button isn\'t cramped to 266px in a 354px viewport.
       (c) Style .register-link ("Bereits angemeldet? Hier einloggen") as
           a full-width outlined button on mobile.
       ──────────────────────────────────────────────────────────── */
    /* (a) Hide menu when registration form is rendered. Structure is */
    /*     .widget--registration > #ae-holder > .widget.open > .menu — */
    /*     so `.widget--registration .menu` matches. C:Hub has the same */
    /*     rule without !important but something is overriding it; */
    /*     adding !important fixes that. */
    '  .widget--registration .menu,',
    '  .widget--login .menu {',
    '    display: none !important;',
    '  }',
    /* Card paddings — tighten so content doesn\'t overflow.
       Reset .b-gr margin so width: 100% renders properly inside form card. */
    '  .c-bx {',
    '    padding: 16px !important;',
    '    margin: 12px !important;',
    '    border-radius: 14px !important;',
    '  }',
    /* (b) Force button-group container full-width — was the root cause of */
    /*     the 266px Registrieren button.                                  */
    '  .b-gr, .b-gr.w-100, .b-gr.fw, .b-gr.mxw-50 {',
    '    width: 100% !important;',
    '    max-width: 100% !important;',
    '    margin-left: 0 !important;',
    '    margin-right: 0 !important;',
    '    padding-left: 0 !important;',
    '    padding-right: 0 !important;',
    '    box-sizing: border-box !important;',
    '  }',
    /* All primary/secondary action buttons go full-width with thumb-friendly height */
    '  .b-gr button.btn-secondary, .b-gr a.btn-a.btn-secondary,',
    '  button.btn-secondary, .btn-secondary,',
    '  .b-gr button.btn-primary, button.btn-primary, .btn-primary,',
    '  .b-gr button, .b-gr a.btn-a,',
    '  button[type="submit"], a[role="button"] {',
    '    width: 100% !important;',
    '    max-width: 100% !important;',
    '    min-height: 48px !important;',
    '    padding: 14px 18px !important;',
    '    font-size: 15px !important;',
    '    box-sizing: border-box !important;',
    '    display: flex !important;',
    '    align-items: center !important;',
    '    justify-content: center !important;',
    '    margin: 8px 0 !important;',
    '  }',
    /* (c) "Bereits angemeldet? Hier einloggen" / "Already signed up?" link */
    /*     C:Hub renders this as an inline-block outlined button.            */
    '  .register-link {',
    '    margin-top: 18px !important;',
    '    text-align: center !important;',
    '    width: 100% !important;',
    '    padding: 0 12px !important;',
    '    box-sizing: border-box !important;',
    '  }',
    '  .register-link a {',
    '    display: block !important;',
    '    width: 100% !important;',
    '    box-sizing: border-box !important;',
    '    text-align: center !important;',
    '    padding: 14px 20px !important;',
    '    margin: 0 !important;',
    '    border-radius: 10px !important;',
    '  }',
    /* Stack adjacent buttons vertically with breathing room — prevents overlap */
    '  .actions, .btn-row, .button-row, [class*="actions"], [class*="btn-group"] {',
    '    display: flex !important;',
    '    flex-direction: column !important;',
    '    gap: 10px !important;',
    '    width: 100% !important;',
    '  }',
    /* Inputs full-width with comfortable mobile padding */
    '  input, select, textarea {',
    '    width: 100% !important;',
    '    min-height: 46px !important;',
    '    padding: 12px 14px !important;',
    '    font-size: 16px !important;',  /* 16px prevents iOS auto-zoom on focus */
    '    box-sizing: border-box !important;',
    '  }',
    /* Form rows — stop horizontal field pairs (e.g. first+last name) */
    '  .form-row, .field-row, [class*="row"] {',
    '    flex-direction: column !important;',
    '    gap: 12px !important;',
    '  }',
    /* Labels — readable size */
    '  label, .label {',
    '    font-size: 13px !important;',
    '    font-weight: 600 !important;',
    '    margin-bottom: 6px !important;',
    '  }',
    /* Headings — scale down for mobile */
    '  .heading-md, .c-hd, .c-inr h1, .c-inr h2 {',
    '    font-size: 20px !important;',
    '    line-height: 1.3 !important;',
    '  }',
    /* Body text */
    '  .body-md, .body-bold, p {',
    '    font-size: 14px !important;',
    '    line-height: 1.55 !important;',
    '  }',
    /* "Already have account?" / register/forgot links — center + readable */
    '  .register-link, .forgot-password, .switch-link {',
    '    text-align: center !important;',
    '    margin-top: 14px !important;',
    '    width: 100% !important;',
    '  }',
    /* Post-signup welcome buttons (continue / browse / go to account) */
    '  .welcome-actions a, .welcome-actions button,',
    '  .post-signup a, .post-signup button,',
    '  [class*="welcome"] a, [class*="welcome"] button {',
    '    width: 100% !important;',
    '    min-height: 48px !important;',
    '    margin: 6px 0 !important;',
    '  }',
    /* Prevent horizontal scroll inside the overlay on mobile */
    '  body, .c-bx, .b-gr, form { max-width: 100vw !important; overflow-x: hidden !important; }',
    '}'
  ].join('\n');

  function injectIntoShadow(shadowRoot) {
    if (!shadowRoot) return false;
    var existing = shadowRoot.getElementById(STYLE_ID);
    if (existing) {
      // Already injected — make sure it's the latest version by re-applying
      existing.textContent = BRAND_CSS + '\n' + FAB_HIDE_CSS;
      return true;
    }
    var styleEl = document.createElement('style');
    styleEl.id = STYLE_ID;
    styleEl.textContent = BRAND_CSS + '\n' + FAB_HIDE_CSS;
    shadowRoot.appendChild(styleEl);
    return true;
  }

  /* ─────────────────────────────────────────────────────────────
     SM24 — Hide C:Hub Floating Action Button (FAB)
     User requested 2026-05-12: header has Sign In link, the bottom-
     right floating button is redundant. Keep the drawer functional
     (URL hash #account:a:login still works) but hide the FAB icon.
     ───────────────────────────────────────────────────────────── */
  var FAB_HIDE_CSS = [
    /* C:Hub uses class .floating-btn (confirmed from accounts-everywhere.css.js).
       Hide it and its icon. Do NOT hide .widget or .overlay — those are the
       drawer + backdrop and must stay functional. */
    '.floating-btn,',
    '.floating-btn-icon { display: none !important; visibility: hidden !important; opacity: 0 !important; pointer-events: none !important; }'
  ].join('\n');

  /* Inject the FAB-hide CSS at TOP LEVEL too — covers the case where C:Hub
     renders the FAB as a sibling of <accounts-everywhere> (outside shadow DOM). */
  function injectTopLevelFabHide() {
    var ID = 'sm24-chub-hide-fab-toplevel';
    if (document.getElementById(ID)) return;
    var style = document.createElement('style');
    style.id = ID;
    style.textContent = [
      /* Top-level C:Hub FAB hide. Class confirmed as .floating-btn from
         accounts-everywhere.css.js source. Keep .widget + .overlay alive
         (those are the drawer + backdrop). */
      '.floating-btn, .floating-btn-icon {',
      '  display: none !important;',
      '  visibility: hidden !important;',
      '  pointer-events: none !important;',
      '}'
    ].join('\n');
    document.head.appendChild(style);
  }

  function styleAccountsEverywhere() {
    /* SM24 2026-05-12: extend to also style <accounts-hub> shadow DOM
       (Hub Page mode, enabled in C:Hub admin). Previously the Hub Page
       rendered with blank/invisible nav text because no brand CSS reached
       its shadow root. Same BRAND_CSS + FAB_HIDE_CSS + a small safety net
       to ensure menu items have visible text. */
    var widgets = document.querySelectorAll('accounts-everywhere, accounts-hub');
    var anyStyled = false;
    widgets.forEach(function (w) {
      if (w.shadowRoot) {
        if (injectIntoShadow(w.shadowRoot)) anyStyled = true;
      }
    });
    return anyStyled;
  }

  /* ─────────────────────────────────────────────────────────────
     SM24 — Translation layer
     C:Hub doesn't follow Weglot/Shopify locale automatically because
     the widget runs in shadow DOM (isolated from page-level translators).
     We replace known English strings with German when site lang is 'de'.
     ───────────────────────────────────────────────────────────── */

  // English → German translation dictionary for C:Hub widget UI
  var TRANSLATIONS = {
    // Top-level menu tabs
    'Homebase': 'Übersicht',
    'Orders': 'Bestellungen',
    'Profile': 'Profil',
    'Logout': 'Abmelden',
    'Wishlist': 'Wunschliste',
    // Headings + greetings
    'Welcome': 'Willkommen',
    'Welcome back': 'Willkommen zurück',
    'My Account': 'Mein Konto',
    'Account': 'Konto',
    // Subtitles + descriptions
    'Track orders, your shopping history & more!': 'Bestellungen verfolgen, Einkaufsverlauf & mehr!',
    'Manage your orders and track deliveries': 'Verwalten Sie Ihre Bestellungen und verfolgen Sie Lieferungen',
    'Your personal info & preferences': 'Ihre persönlichen Daten & Einstellungen',
    'Items you saved for later': 'Artikel, die Sie für später gespeichert haben',
    'Purchase faster, track orders and save your shopping history': 'Schneller einkaufen, Bestellungen verfolgen und Einkaufsverlauf speichern',
    'Already Signed Up? Sign In here': 'Bereits registriert? Hier anmelden',
    'New Customer? Sign Up here': 'Neukunde? Hier registrieren',
    // CTA buttons
    'Go to Sign In': 'Zum Anmelden',
    'Sign In': 'Anmelden',
    'Sign in': 'Anmelden',
    'Sign Up': 'Registrieren',
    'Sign up': 'Registrieren',
    'Sign Out': 'Abmelden',
    'Sign out': 'Abmelden',
    'Log Out': 'Abmelden',
    'Log out': 'Abmelden',
    'Login': 'Anmelden',
    'Register': 'Registrieren',
    'Create Account': 'Konto erstellen',
    'Save': 'Speichern',
    'Cancel': 'Abbrechen',
    'Edit': 'Bearbeiten',
    'Delete': 'Löschen',
    'Update': 'Aktualisieren',
    'Continue': 'Weiter',
    'Back': 'Zurück',
    'Close': 'Schließen',
    // Recently viewed / saved
    'Recently viewed': 'Zuletzt angesehen',
    'Recently Viewed': 'Zuletzt angesehen',
    'No recently viewed items': 'Keine kürzlich angesehenen Artikel',
    'Saved from cart': 'Aus Warenkorb gespeichert',
    'No items saved': 'Keine gespeicherten Artikel',
    'No items saved from cart': 'Keine gespeicherten Artikel',
    'No items in your cart yet': 'Noch keine Artikel im Warenkorb',
    // Orders
    'Order History': 'Bestellverlauf',
    'Order': 'Bestellung',
    'No orders yet': 'Noch keine Bestellungen',
    'View Order': 'Bestellung ansehen',
    'Order Details': 'Bestelldetails',
    'Track Order': 'Sendung verfolgen',
    'Reorder': 'Erneut bestellen',
    'Buy Again': 'Erneut kaufen',
    // Profile fields
    'First Name': 'Vorname',
    'Last Name': 'Nachname',
    'Email': 'E-Mail',
    'Email Address': 'E-Mail-Adresse',
    'Phone': 'Telefon',
    'Phone Number': 'Telefonnummer',
    'Password': 'Passwort',
    'Address': 'Adresse',
    'Addresses': 'Adressen',
    'City': 'Stadt',
    'Country': 'Land',
    'Postal Code': 'PLZ',
    // Status messages
    'Loading...': 'Wird geladen...',
    'Loading': 'Wird geladen',
    'Saved': 'Gespeichert',
    'Updated': 'Aktualisiert',
    'Error': 'Fehler',
    'Success': 'Erfolg',
    // Misc
    'Help': 'Hilfe',
    'Contact': 'Kontakt',
    'Settings': 'Einstellungen',
    'Notifications': 'Benachrichtigungen',
    'Notification preferences': 'Benachrichtigungseinstellungen',
    'Email marketing': 'E-Mail-Marketing',
    // Hub Page — left nav + profile sidebar
    'For You': 'Für Sie',
    'My Profile': 'Mein Profil',
    'No Default Address': 'Keine Standardadresse',
    'Default Address': 'Standardadresse',
    'Add Address': 'Adresse hinzufügen',
    'Edit Address': 'Adresse bearbeiten',
    'Add New Address': 'Neue Adresse hinzufügen',
    'My Address Book': 'Mein Adressbuch',
    'NO ADDRESSES': 'KEINE ADRESSEN',
    'No matching Orders': 'Keine passenden Bestellungen',
    'You have not placed any orders.': 'Sie haben noch keine Bestellungen aufgegeben.',
    // Hub Page — orders filter/sort
    'Filter by Order # or Product Name': 'Nach Bestellnummer oder Produkt filtern',
    'Filter by Order or Product Name': 'Nach Bestellnummer oder Produkt filtern',
    'Showing': 'Anzeige',
    'Showing 0 Orders': 'Anzeige: 0 Bestellungen',
    'matching': 'passend',
    'Newest': 'Neueste',
    'Oldest': 'Älteste',
    'Sort by': 'Sortieren nach',
    'Sort': 'Sortieren',
    'Order Value (High to Low)': 'Bestellwert (hoch nach niedrig)',
    'Order Value (Low to High)': 'Bestellwert (niedrig nach hoch)',
    'Order Value': 'Bestellwert',
    'High to Low': 'hoch nach niedrig',
    'Low to High': 'niedrig nach hoch',
    'Price (High to Low)': 'Preis (hoch nach niedrig)',
    'Price (Low to High)': 'Preis (niedrig nach hoch)',
    'Price': 'Preis',
    'Date': 'Datum',
    'Status': 'Status',
    'ORDER DATE': 'BESTELLDATUM',
    'ORDER NUMBER': 'BESTELLNUMMER',
    'Download': 'Herunterladen',
    'Pay Now': 'Jetzt bezahlen',
    'CANCELLED ON': 'STORNIERT AM',
    // Misc Hub Page strings
    'Your Last Order': 'Ihre letzte Bestellung',
    'View My Orders': 'Meine Bestellungen anzeigen',
    'View Orders': 'Bestellungen anzeigen',
    'Please place an order to see your order history.': 'Bitte geben Sie eine Bestellung auf, um Ihren Bestellverlauf zu sehen.',
    'Home': 'Startseite',
    'default': 'Standard',
    'more items': 'weitere Artikel',
    'Recent Orders': 'Letzte Bestellungen',
    'Your most recently view products will appear here? Nice and easy for you to view next time!': 'Ihre zuletzt angesehenen Produkte erscheinen hier — schnell wiederzufinden.',
    'Start Shopping': 'Jetzt einkaufen',
    'Continue Shopping': 'Weiter einkaufen',
    'Shop Now': 'Jetzt einkaufen',
    'Browse Products': 'Produkte ansehen',
    'View All': 'Alle anzeigen',
    'See All': 'Alle anzeigen',
    'Show More': 'Mehr anzeigen',
    'Show Less': 'Weniger anzeigen',
    // Hub Page — Profile sub-views
    'My Preferences': 'Meine Einstellungen',
    'Preferences': 'Einstellungen',
    'My Settings': 'Meine Einstellungen',
    'Saved Addresses': 'Gespeicherte Adressen',
    'Address Book': 'Adressbuch',
    'Change my Password': 'Passwort ändern',
    'Change Password': 'Passwort ändern',
    'Change my password': 'Passwort ändern',
    'Reset Password': 'Passwort zurücksetzen',
    'Forgot Password?': 'Passwort vergessen?',
    'Forgot your password?': 'Passwort vergessen?',
    'Current Password': 'Aktuelles Passwort',
    'New Password': 'Neues Passwort',
    'Confirm Password': 'Passwort bestätigen',
    'Confirm New Password': 'Neues Passwort bestätigen',
    'E-Mail (read only)': 'E-Mail (nur lesen)',
    'Email (read only)': 'E-Mail (nur lesen)',
    'read only': 'nur lesen',
    '(read only)': '(nur lesen)',
    'Read only': 'Nur lesen',
    'Read Only': 'Nur lesen',
    'Save Changes': 'Änderungen speichern',
    'Update Profile': 'Profil aktualisieren',
    'Personal Information': 'Persönliche Informationen',
    'Personal Info': 'Persönliche Daten',
    'Account Information': 'Kontoinformationen',
    'Communication Preferences': 'Kommunikationseinstellungen',
    'Marketing Emails': 'Marketing-E-Mails',
    'Subscribe': 'Abonnieren',
    'Unsubscribe': 'Abbestellen',
    'Subscribed': 'Abonniert',
    'Date of Birth': 'Geburtsdatum',
    'Gender': 'Geschlecht',
    'Male': 'Männlich',
    'Female': 'Weiblich',
    'Other': 'Andere',
    'Prefer not to say': 'Keine Angabe'
  };

  function detectLang() {
    // 1) URL path: /en/ → en, anything else → de (your site default)
    var path = window.location.pathname;
    if (/^\/en(\/|$)/.test(path)) return 'en';
    // 2) html lang attribute (Weglot updates this)
    var htmlLang = (document.documentElement.lang || '').toLowerCase();
    if (htmlLang.indexOf('en') === 0) return 'en';
    if (htmlLang.indexOf('de') === 0) return 'de';
    // 3) default to German (DACH primary market)
    return 'de';
  }

  function translateNode(node, lang) {
    if (lang !== 'de') return; // only DE has a dictionary for now
    /* SM24 2026-05-12: also translate input/textarea placeholders, button
       titles, and aria-labels so things like "Filter by Order # or Product
       Name" (a placeholder, not a text node) get translated too. */
    var attrEls = node.querySelectorAll ? node.querySelectorAll('input[placeholder], textarea[placeholder], [title], [aria-label]') : [];
    attrEls.forEach(function(el){
      ['placeholder', 'title', 'aria-label'].forEach(function(attr){
        var v = el.getAttribute && el.getAttribute(attr);
        if (v && TRANSLATIONS[v.trim()]) el.setAttribute(attr, TRANSLATIONS[v.trim()]);
      });
    });
    var walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null);
    var current;
    while ((current = walker.nextNode())) {
      var raw = current.nodeValue;
      var trimmed = raw.trim();
      if (!trimmed) continue;
      if (TRANSLATIONS[trimmed]) {
        // Preserve surrounding whitespace
        var leading = raw.match(/^\s*/)[0];
        var trailing = raw.match(/\s*$/)[0];
        current.nodeValue = leading + TRANSLATIONS[trimmed] + trailing;
      }
    }
  }

  /* SM24 2026-06-22: Register-screen heading must show "free" (free-registration
     standing rule). Targets ONLY the C:Hub signup heading (.c-hd.heading-md whose
     text is "Sign Up" / "Registrieren") inside the widget shadow root; leaves
     "Welcome" and "Sign In" untouched. Runs on EN + DE; idempotent (won't re-match
     once rewritten, and works whichever order it runs vs the DE translation). */
  function freeSignupHeading(root) {
    if (!root || !root.querySelectorAll) return;
    /* Locale by URL path only (the store's canonical rule: /en/* = English, else
       German). htmlLang is unreliable on /a/customerhub — it can flip to "en" and
       diverge from the rest of the UI, so we do NOT use detectLang() here. */
    var isEn = /^\/en(\/|$)/.test(window.location.pathname);
    var freeText = isEn ? 'Sign Up for Free' : 'Kostenlos registrieren';
    root.querySelectorAll('.c-hd.heading-md').forEach(function (h) {
      var t = (h.textContent || '').trim();
      if (t === 'Sign Up' || t === 'Registrieren') h.textContent = freeText;
    });
  }

  function translateAccountsEverywhere() {
    var lang = detectLang();
    document.querySelectorAll('accounts-everywhere, accounts-hub').forEach(function (w) {
      if (w.shadowRoot) { translateNode(w.shadowRoot, lang); freeSignupHeading(w.shadowRoot); }
    });
  }

  /* ─────────────────────────────────────────────────────────────
     SM24 2026-05-22 — Customer Hub sidebar label population.
     C:Hub's Customer Hub left-side menu renders <a><span></span></a>
     items with EMPTY spans on this store (verified via DOM inspect:
     <a id="ae-menu-0" href="#account:a:dashboard"><span></span></a>).
     Whatever was meant to populate the label text in the C:Hub
     widget isn't firing — likely a missing app-config string or
     a locale-bundle that didn't load. We backfill the labels
     ourselves based on each item's href.
     ───────────────────────────────────────────────────────────── */
  var HUB_NAV_LABELS = {
    de: {
      '#account:a:dashboard': 'Übersicht',
      '#account:a:home': 'Übersicht',
      '#account:a:homebase': 'Übersicht',
      '#account:a:orders': 'Bestellungen',
      '#account:a:profile': 'Profil',
      '#account:a:account': 'Profil',
      '#account:a:logout': 'Abmelden',
      '#account:a:signout': 'Abmelden',
      '#account:a:wishlist': 'Wunschliste',
      '#account:a:addresses': 'Adressen',
      '#account:a:saved': 'Gespeichert',
      '#account:a:recently_viewed': 'Zuletzt angesehen',
      '#account:a:settings': 'Einstellungen'
    },
    en: {
      '#account:a:dashboard': 'Dashboard',
      '#account:a:home': 'Home',
      '#account:a:homebase': 'Home',
      '#account:a:orders': 'Orders',
      '#account:a:profile': 'Profile',
      '#account:a:account': 'Account',
      '#account:a:logout': 'Sign Out',
      '#account:a:signout': 'Sign Out',
      '#account:a:wishlist': 'Wishlist',
      '#account:a:addresses': 'Addresses',
      '#account:a:saved': 'Saved',
      '#account:a:recently_viewed': 'Recently Viewed',
      '#account:a:settings': 'Settings'
    }
  };

  function populateHubMenuLabelsInRoot(root) {
    if (!root || !root.querySelectorAll) return;
    var lang = detectLang();
    var labels = HUB_NAV_LABELS[lang] || HUB_NAV_LABELS.de;
    var items = root.querySelectorAll('.menu #menuItems .inner-menu a, .inner-menu a[href*="#account:a:"], a[id^="ae-menu-"]');
    items.forEach(function (a) {
      var href = a.getAttribute('href') || '';
      // Normalize href to lowercase action key (the part starting at #account:a:)
      var key = (href.match(/#account:a:[a-z_]+/i) || [''])[0].toLowerCase();
      var text = labels[key];
      if (!text) return;
      var span = a.querySelector('span');
      // If no span exists, create one (defensive — C:Hub always renders one)
      if (!span) {
        span = document.createElement('span');
        a.appendChild(span);
      }
      var current = (span.textContent || '').trim();
      // Only populate if span is empty OR contains the OTHER-locale label
      // (so a language switch re-applies the right one).
      var otherLang = lang === 'de' ? 'en' : 'de';
      var otherLabels = HUB_NAV_LABELS[otherLang] || {};
      var otherText = otherLabels[key];
      if (!current || (otherText && current === otherText)) {
        span.textContent = text;
      }
    });
  }

  function populateHubMenuLabels() {
    document.querySelectorAll('accounts-everywhere, accounts-hub').forEach(function (w) {
      if (w.shadowRoot) populateHubMenuLabelsInRoot(w.shadowRoot);
    });
  }

  /* ─────────────────────────────────────────────────────────────
     SM24 2026-05-21 — Forgot Password override
     Froonze C:Hub's built-in "Forgot password?" click handler is
     broken on this store: clicking it flickers the drawer and
     reloads back to Sign In without sending a reset email. We
     intercept the click inside the shadow DOM and submit the
     recover form directly to Shopify's /account/recover endpoint.
     ───────────────────────────────────────────────────────────── */
  function sm24OpenForgotModal(prefilledEmail) {
    var existing = document.getElementById('sm24-forgot-modal');
    if (existing) existing.remove();
    var lang = detectLang();
    var t = (lang === 'de') ? {
      title: 'Passwort zurücksetzen',
      subtitle: 'Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.',
      email_label: 'E-Mail-Adresse',
      email_placeholder: 'name@beispiel.de',
      submit: 'Link zum Zurücksetzen senden',
      sending: 'Wird gesendet …',
      back: 'Zurück zur Anmeldung',
      success_title: 'E-Mail gesendet',
      success_body: 'Wenn ein Konto mit dieser E-Mail-Adresse existiert, erhalten Sie in Kürze eine E-Mail zum Zurücksetzen des Passworts. Bitte prüfen Sie auch Ihren Spam-Ordner.',
      error_title: 'Fehler',
      error_body: 'Die E-Mail zum Zurücksetzen konnte nicht gesendet werden. Bitte versuchen Sie es später erneut oder kontaktieren Sie info@shopmed24.de.',
      invalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
      close: 'Schließen',
      already_sent_title: 'Anfrage bereits gesendet',
      already_sent_body: 'Wir haben kürzlich eine E-Mail zum Zurücksetzen des Passworts an diese Adresse gesendet. Bitte prüfen Sie Ihr Postfach und Ihren Spam-Ordner. Wenn Sie keine E-Mail erhalten, versuchen Sie es bitte in {min} Minuten erneut.',
      request_sent_title: 'Anfrage gesendet',
      request_sent_body: 'Wenn ein Konto mit dieser E-Mail-Adresse existiert, erhalten Sie in Kürze einen Link zum Zurücksetzen des Passworts. Bitte prüfen Sie auch Ihren Spam-Ordner. Bei Problemen wenden Sie sich an info@shopmed24.de.'
    } : {
      title: 'Reset Password',
      subtitle: 'Enter your email address. We will send you a link to reset your password.',
      email_label: 'Email address',
      email_placeholder: 'name@example.com',
      submit: 'Send reset link',
      sending: 'Sending …',
      back: 'Back to Sign In',
      success_title: 'Email sent',
      success_body: 'If an account exists with this email, you will receive a password-reset link shortly. Please also check your spam folder.',
      error_title: 'Error',
      error_body: 'The reset email could not be sent. Please try again later or contact info@shopmed24.de.',
      invalid: 'Please enter a valid email address.',
      close: 'Close',
      already_sent_title: 'Request already sent',
      already_sent_body: 'We recently sent a password-reset email to this address. Please check your inbox and spam folder. If you don\'t receive it, please try again in {min} minutes.',
      request_sent_title: 'Request sent',
      request_sent_body: 'If an account exists with this email, you will receive a password-reset link shortly. Please also check your spam folder. For issues, contact info@shopmed24.de.'
    };
    var safePrefill = (prefilledEmail || '').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    var modal = document.createElement('div');
    modal.id = 'sm24-forgot-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.innerHTML = ''
      + '<style>'
      + '#sm24-forgot-modal{position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;background:rgba(15,23,42,.65);font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;animation:sm24fm-fade .18s ease;isolation:isolate}'
      + '#sm24-forgot-modal::before{content:"";position:absolute;inset:0;z-index:-1;backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px)}'
      + '@keyframes sm24fm-fade{from{opacity:0}to{opacity:1}}'
      + '@keyframes sm24fm-slide{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}'
      + '#sm24-forgot-modal .sm24fm-card{background:#fff;border-radius:18px;width:calc(100% - 32px);max-width:440px;box-shadow:0 25px 50px -12px rgba(0,0,0,.35);overflow:hidden;animation:sm24fm-slide .24s ease}'
      + '#sm24-forgot-modal .sm24fm-head{display:flex;align-items:center;justify-content:space-between;padding:18px 22px 14px;border-bottom:1px solid rgba(141,185,202,.25)}'
      + '#sm24-forgot-modal .sm24fm-title{font-size:19px;font-weight:800;color:#4b7381;margin:0;letter-spacing:-.01em}'
      + '#sm24-forgot-modal .sm24fm-close{width:32px;height:32px;border-radius:8px;border:none;background:transparent;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;color:#45474d;transition:background .15s,transform .2s}'
      + '#sm24-forgot-modal .sm24fm-close:hover{background:#f4f8fa;transform:rotate(90deg)}'
      + '#sm24-forgot-modal .sm24fm-close svg{width:16px;height:16px;stroke:currentColor;stroke-width:2;stroke-linecap:round;fill:none}'
      + '#sm24-forgot-modal .sm24fm-body{padding:22px}'
      + '#sm24-forgot-modal .sm24fm-sub{font-size:14px;color:#45474d;line-height:1.55;margin:0 0 18px}'
      + '#sm24-forgot-modal .sm24fm-label{display:block;font-size:13px;font-weight:700;color:#1f2e35;margin:0 0 8px}'
      + '#sm24-forgot-modal .sm24fm-label::after{content:" *";color:#dc2626;font-weight:600}'
      + '#sm24-forgot-modal .sm24fm-input{width:100%;padding:13px 14px;font-size:15px;font-family:inherit;color:#1f2e35;background:#fff;border:1.5px solid #d1d5db;border-radius:10px;outline:none;transition:border-color .15s,box-shadow .15s;box-sizing:border-box}'
      + '#sm24-forgot-modal .sm24fm-input:focus{border-color:#8db9ca;box-shadow:0 0 0 3px rgba(141,185,202,.25)}'
      + '#sm24-forgot-modal .sm24fm-submit{display:flex;align-items:center;justify-content:center;width:100%;padding:14px 20px;margin-top:18px;font-family:inherit;font-size:15px;font-weight:700;color:#fff;background:linear-gradient(135deg,#6ba4b8,#6ba4b8);border:none;border-radius:12px;cursor:pointer;transition:filter .15s,transform .1s,box-shadow .15s;box-shadow:0 4px 12px rgba(75,115,129,.20)}'
      + '#sm24-forgot-modal .sm24fm-submit:hover{filter:brightness(1.08);box-shadow:0 6px 16px rgba(75,115,129,.30)}'
      + '#sm24-forgot-modal .sm24fm-submit:active{transform:translateY(1px)}'
      + '#sm24-forgot-modal .sm24fm-submit[disabled]{opacity:.6;cursor:not-allowed}'
      + '#sm24-forgot-modal .sm24fm-back{display:block;width:100%;margin-top:12px;padding:12px 16px;font-family:inherit;font-size:14px;font-weight:600;color:#6ba4b8;background:transparent;border:1.5px solid rgba(141,185,202,.4);border-radius:12px;cursor:pointer;text-align:center;transition:background .15s,border-color .15s}'
      + '#sm24-forgot-modal .sm24fm-back:hover{background:#f4f8fa;border-color:#8db9ca}'
      + '#sm24-forgot-modal .sm24fm-result{padding:8px 0 0;margin:0;font-size:14px;line-height:1.55}'
      + '#sm24-forgot-modal .sm24fm-result.ok{color:#4b7381}'
      + '#sm24-forgot-modal .sm24fm-result.err{color:#b91c1c}'
      + '#sm24-forgot-modal .sm24fm-result strong{display:block;font-size:15px;margin-bottom:4px}'
      + '</style>'
      + '<div class="sm24fm-card" role="document">'
        + '<div class="sm24fm-head">'
          + '<h2 class="sm24fm-title">' + t.title + '</h2>'
          + '<button type="button" class="sm24fm-close" aria-label="' + t.close + '">'
            + '<svg viewBox="0 0 24 24" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>'
          + '</button>'
        + '</div>'
        + '<form class="sm24fm-body" id="sm24-forgot-form" novalidate>'
          + '<p class="sm24fm-sub">' + t.subtitle + '</p>'
          + '<label class="sm24fm-label" for="sm24-forgot-email">' + t.email_label + '</label>'
          + '<input type="email" class="sm24fm-input" id="sm24-forgot-email" name="email" autocomplete="email" required placeholder="' + t.email_placeholder + '" value="' + safePrefill + '">'
          + '<div class="sm24fm-result" id="sm24-forgot-result" hidden></div>'
          + '<button type="submit" class="sm24fm-submit" id="sm24-forgot-submit">' + t.submit + '</button>'
          + '<button type="button" class="sm24fm-back" id="sm24-forgot-back">' + t.back + '</button>'
        + '</form>'
      + '</div>';
    document.body.appendChild(modal);
    // Hide every Sign In drawer / C:Hub overlay while this modal is open.
    // These elements sit at high z-index in their own stacking context and
    // their backdrop-filter rendered ABOVE our modal, blurring its content.
    // Selector covers: custom sm24-clogin drawer, C:Hub native web components
    // (accounts-everywhere, accounts-hub, customer-hub), and any drawer/overlay
    // root whose class contains the typical wrapper names.
    var __sm24DrawerSelector = 'accounts-everywhere, accounts-hub, customer-hub, [class*="sm24-clogin"], [class*="sm24-customer-login"], [class*="customerhub"], [class*="customer-hub"], [class*="froonze"], [id*="froonze"]';
    document.querySelectorAll(__sm24DrawerSelector).forEach(function (el) {
      if (el.id === 'sm24-forgot-modal' || el.closest && el.closest('#sm24-forgot-modal')) return;
      if (!el.hasAttribute('data-sm24-prev-display')) {
        el.setAttribute('data-sm24-prev-display', el.style.display || '');
      }
      el.style.setProperty('display', 'none', 'important');
    });
    var emailField = modal.querySelector('#sm24-forgot-email');
    var submitBtn = modal.querySelector('#sm24-forgot-submit');
    var resultEl = modal.querySelector('#sm24-forgot-result');
    var form = modal.querySelector('#sm24-forgot-form');
    function close() {
      modal.remove();
      document.removeEventListener('keydown', onKey);
      document.querySelectorAll(__sm24DrawerSelector).forEach(function (el) {
        if (el.hasAttribute('data-sm24-prev-display')) {
          var prev = el.getAttribute('data-sm24-prev-display');
          if (prev) { el.style.display = prev; } else { el.style.removeProperty('display'); }
          el.removeAttribute('data-sm24-prev-display');
        }
      });
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);
    modal.querySelector('.sm24fm-close').addEventListener('click', close);
    modal.querySelector('#sm24-forgot-back').addEventListener('click', close);
    modal.addEventListener('click', function (e) { if (e.target === modal) close(); });
    setTimeout(function () { try { emailField.focus(); if (emailField.value) emailField.select(); } catch (err) {} }, 50);

    // SM24 2026-05-23 — Pre-warm Shopify's invisible CAPTCHA on the hidden
    // recover form. Shopify's captcha-bootstrap binds CAPTCHA on focusin /
    // change events on the form. Without this pre-warm, the first submit
    // gets preventDefault'd by CAPTCHA (which is async-loading hCaptcha),
    // leaving the modal stuck on "Wird gesendet …". Dispatching focusin +
    // change events on the hidden email input triggers binding immediately
    // so by the time the user clicks submit, CAPTCHA is ready.
    setTimeout(function () {
      try {
        var hiddenEmail = document.getElementById('sm24-recover-form-email');
        if (hiddenEmail) {
          hiddenEmail.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
          hiddenEmail.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } catch (err) {}
    }, 100);
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (emailField.value || '').trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        resultEl.hidden = false;
        resultEl.className = 'sm24fm-result err';
        resultEl.innerHTML = '<strong>' + t.error_title + '</strong>' + t.invalid;
        try { emailField.focus(); } catch (err) {}
        return false;
      }

      // SM24_RECOVER_COOLDOWN_v1 2026-05-27
      // Per-email cooldown of 15 minutes stored in localStorage.
      // BACKGROUND: Shopify's /account/recover endpoint enforces a per-email
      // rate limit (~5 attempts per 15 min). When the user has triggered the
      // limit, EVERY further submit re-extends the block AND a "Too many
      // attempts" error renders even if Shopify's backend still managed to
      // queue/send an email from earlier attempts. This shows up as the
      // confusing "I got the email but I see an error page" symptom.
      // Solution: block re-submission of the same email for 15 min. Show a
      // friendly "already requested, check inbox" banner instead of letting
      // the user keep extending the rate limit.
      var emailKey = email.toLowerCase();
      var cooldownKey = 'sm24_recover_cd_' + emailKey;
      var COOLDOWN_MS = 15 * 60 * 1000; // 15 minutes
      try {
        var lastAttempt = parseInt(localStorage.getItem(cooldownKey) || '0', 10);
        if (lastAttempt && (Date.now() - lastAttempt) < COOLDOWN_MS) {
          var remainingMin = Math.ceil((COOLDOWN_MS - (Date.now() - lastAttempt)) / 60000);
          resultEl.hidden = false;
          resultEl.className = 'sm24fm-result ok';
          resultEl.innerHTML = '<strong>' + t.already_sent_title + '</strong>' + t.already_sent_body.replace('{min}', remainingMin);
          return false;
        }
      } catch (err) {}

      submitBtn.disabled = true;
      submitBtn.textContent = t.sending;
      resultEl.hidden = true;
      // Mark cooldown timestamp BEFORE submit so that a refresh / back-button
      // doesn't allow immediate resubmit even if the form was canceled.
      try { localStorage.setItem(cooldownKey, String(Date.now())); } catch (err) {}
      // SM24 2026-05-23 — Shopify added invisible hCaptcha to /account/recover.
      // A hand-built <form> (the previous approach) is rejected with
      // "Missing CAPTCHA token" because Shopify needs the hidden CAPTCHA-token
      // field that ONLY gets emitted by the {% form 'recover_customer_password' %}
      // Liquid tag. We pre-render that form (hidden) in
      // sections/sm24-customer-login.liquid and submit it here so the CAPTCHA
      // token is included. Top-level submit is still required (Cloudflare
      // mitigation still blocks fetch POSTs).
      var shopifyRecoverForm = document.getElementById('sm24-recover-form');
      var shopifyRecoverEmail = document.getElementById('sm24-recover-form-email');
      try {
        sessionStorage.setItem('sm24_recover_email', email);
        sessionStorage.setItem('sm24_recover_just_submitted', '1');
      } catch (err) {}
      if (shopifyRecoverForm && shopifyRecoverEmail) {
        shopifyRecoverEmail.value = email;
        // Also dispatch a change event so CAPTCHA bootstrap sees the input.
        try {
          shopifyRecoverEmail.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (err) {}

        // CRITICAL: must use requestSubmit() — NOT submit().
        // Shopify's captcha-bootstrap listens for the 'submit' EVENT to
        // intercept, add form_key + persist data to sessionStorage, then
        // submit. form.submit() does NOT fire the submit event (DOM quirk),
        // so CAPTCHA never runs → "Missing CAPTCHA token" 400.
        // requestSubmit() fires the event correctly.
        //
        // SM24_RECOVER_NO_RETRY_LOOP_v1 2026-05-27: REWROTE the old retry-up-to-10
        // logic which was hammering /account/recover with multiple submits
        // before Shopify's CAPTCHA had finished validating, causing persistent
        // "Too many attempts" rate-limit blocks (per-email + per-IP) that
        // survived for DAYS across devices. The old loop fired retries at
        // 350ms intervals — far too fast for CAPTCHA (which takes 1-3s).
        //
        // New approach: ONE submit attempt. Wait 5 seconds. If the modal is
        // still there (meaning navigation never happened), show a friendly
        // error asking the user to try again manually — do NOT auto-retry.
        // The single submit is enough because CAPTCHA bootstrap binds on
        // DOMContentLoaded and the first requestSubmit() fires the submit
        // event the bootstrap listener was waiting for.
        function tryShopifySubmit() {
          if (typeof shopifyRecoverForm.requestSubmit === 'function') {
            shopifyRecoverForm.requestSubmit();
          } else {
            var sb = shopifyRecoverForm.querySelector('button[type="submit"], input[type="submit"]');
            if (sb) sb.click(); else shopifyRecoverForm.submit();
          }
          // Give CAPTCHA + Shopify backend a full 8 seconds. Successful submit
          // navigates away long before this timer fires. If we're still on
          // this page after 8s, show error — do NOT re-submit (the per-email
          // rate limit would only get worse).
          setTimeout(function () {
            if (document.getElementById('sm24-forgot-modal')) {
              try {
                submitBtn.disabled = false;
                submitBtn.textContent = t.submit;
                resultEl.hidden = false;
                resultEl.className = 'sm24fm-result ok';
                resultEl.innerHTML = '<strong>' + t.request_sent_title + '</strong>' + t.request_sent_body;
              } catch (err) {}
            }
          }, 8000);
        }
        tryShopifySubmit();
      } else {
        // Fallback: hand-built form (will hit "Missing CAPTCHA token" but at
        // least produces a real error page instead of silent failure).
        var nativeForm = document.createElement('form');
        nativeForm.action = '/account/recover';
        nativeForm.method = 'POST';
        nativeForm.acceptCharset = 'UTF-8';
        nativeForm.style.display = 'none';
        function addField(name, value) {
          var inp = document.createElement('input');
          inp.type = 'hidden';
          inp.name = name;
          inp.value = value;
          nativeForm.appendChild(inp);
        }
        addField('form_type', 'recover_customer_password');
        addField('utf8', '✓');
        addField('email', email);
        document.body.appendChild(nativeForm);
        nativeForm.submit();
      }
    });
  }

  function overrideForgotPasswordInRoot(root) {
    if (!root || !root.querySelectorAll) return;
    var candidates = root.querySelectorAll('a, button, span, div');
    candidates.forEach(function (el) {
      if (el.__sm24ForgotOverridden) return;
      var txt = (el.textContent || '').trim().toLowerCase();
      if (!txt || txt.length > 60) return;
      if (txt.indexOf('forgot') === -1 && txt.indexOf('vergessen') === -1) return;
      var hasClickableChild = false;
      el.querySelectorAll('a, button').forEach(function (c) {
        if ((c.textContent || '').trim().toLowerCase().indexOf(txt) !== -1 && c !== el) hasClickableChild = true;
      });
      if (hasClickableChild) return;
      el.__sm24ForgotOverridden = true;
      el.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        var emailInput = root.querySelector('input[type="email"]')
                      || root.querySelector('input[name="email"]')
                      || root.querySelector('input[name*="email" i]');
        var prefilled = emailInput ? (emailInput.value || '').trim() : '';
        sm24OpenForgotModal(prefilled);
        return false;
      }, true);
    });
  }

  function overrideForgotPasswordEverywhere() {
    // Shadow DOM: C:Hub native widgets
    document.querySelectorAll('accounts-everywhere, accounts-hub').forEach(function (w) {
      if (w.shadowRoot) overrideForgotPasswordInRoot(w.shadowRoot);
    });
    // Regular DOM: sm24-customer-login.liquid drawer + main-b2b-login + any other forgot-password link in the page
    overrideForgotPasswordInRoot(document);
  }

  /* ═══════════════════════════════════════════════════════════════
     SM24_CHUB_TERMS_v1 — 2026-06-07
     REQUIRED "Terms & Conditions" consent checkbox on the C:Hub
     (Froonze) buyer SIGNUP/REGISTRATION form ONLY.

     Goal: the user cannot complete signup without ticking the box.
     We do NOT touch the form action, field names, tokens, or C:Hub's
     native submit logic. We only:
       (1) inject a checkbox + bilingual AGB/Terms label directly ABOVE
           the "Sign Up" submit button,
       (2) keep that submit button visually disabled until checked, AND
       (3) intercept submit/click in capture phase and preventDefault
           while unchecked (belt-and-braces in case C:Hub re-enables the
           button), showing an inline bilingual hint + highlighting the box.
     When checked, signup proceeds 100% normally.

     VIEW DETECTION (register vs login/reset/recover/logged-in):
       - Operate only inside accounts-everywhere/accounts-hub shadow roots.
       - Skip entirely when the nearest #ae-holder has isloggedin="true"
         (dashboard) — consistent with the existing menu-hide logic that
         keys off #ae-holder[isloggedin].
       - The signup form is the ONE logged-out form that has BOTH a
         first-name AND a last-name field (login = email+password only;
         reset/recover = email-only or password-only). We require a
         first-name input, a password input, and a submit button before
         injecting. This excludes the Sign In drawer by design.

     Idempotent: guarded per-form via form.__sm24TermsInjected and a
     unique wrapper id check; re-runs safely on every C:Hub re-render
     via the existing MutationObserver -> applyAll() pipeline.
     ═══════════════════════════════════════════════════════════════ */
  function sm24FindSubmitButton(form) {
    // Prefer an explicit submit control inside the form.
    var btn = form.querySelector('button[type="submit"], input[type="submit"]');
    if (btn) return btn;
    // C:Hub sometimes renders the action as a styled <button> / <a> without
    // type="submit". Match by text (Sign Up / Registrieren / Create Account)
    // or by the brand button classes used elsewhere in this file.
    var candidates = form.querySelectorAll('button, a.btn-a, .btn-secondary, .btn-primary, [role="button"]');
    var wanted = ['sign up', 'registrieren', 'register', 'create account', 'konto erstellen'];
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      var txt = (c.textContent || '').trim().toLowerCase();
      if (!txt) continue;
      for (var j = 0; j < wanted.length; j++) {
        if (txt === wanted[j] || txt.indexOf(wanted[j]) !== -1) return c;
      }
    }
    return null;
  }

  function sm24IsSignupForm(form) {
    // Must be a logged-out registration form: first-name + password + submit.
    if (!form || !form.querySelector) return false;
    var hasFirstName = !!form.querySelector(
      'input[name*="first" i], input[id*="first" i], input[autocomplete="given-name"], input[placeholder*="first" i], input[placeholder*="vorname" i]'
    );
    if (!hasFirstName) return false; // login/reset/recover have no first-name field
    var hasPassword = !!form.querySelector('input[type="password"]');
    if (!hasPassword) return false; // recover (email-only) excluded
    var submit = sm24FindSubmitButton(form);
    if (!submit) return false;
    return true;
  }

  // SM24_CHUB_TERMS_MOBILE_v1 (2026-06-13): the injected consent row lives INSIDE
  // the C:Hub shadow root, where the Froonze component / Xtra theme CSS was
  // overriding our inline styles — the checkbox blew up into a big empty box and
  // the "Ich stimme den AGB zu" label text was pushed out of view on mobile.
  // A <style> in document.head can't cross the shadow boundary, so we inject a
  // scoped, high-specificity !important sheet INTO the same root (once per root).
  // Purely cosmetic: does NOT touch the consent checkbox logic or the submit gate.
  function sm24EnsureTermsStyle(root) {
    try {
      if (!root) return;
      var host = (root.nodeType === 9 || root === document) ? (document.head || document.documentElement) : root;
      if (!host || !host.querySelector || !host.appendChild) return;
      if (host.querySelector('#sm24-terms-style')) return;
      var st = document.createElement('style');
      st.id = 'sm24-terms-style';
      st.textContent = [
        '.sm24-terms-row{display:flex!important;flex-direction:column!important;gap:6px!important;margin:14px 0 10px!important;text-align:left!important;width:100%!important;}',
        '.sm24-terms-row .sm24-terms-label{display:flex!important;flex-direction:row!important;align-items:flex-start!important;justify-content:flex-start!important;gap:9px!important;font-size:13.5px!important;line-height:1.5!important;color:#45474d!important;cursor:pointer!important;text-align:left!important;width:100%!important;margin:0!important;}',
        '.sm24-terms-row input.sm24-terms-cb{-webkit-appearance:checkbox!important;appearance:checkbox!important;position:static!important;float:none!important;flex:0 0 auto!important;width:18px!important;height:18px!important;min-width:18px!important;min-height:18px!important;max-width:18px!important;max-height:18px!important;margin:1px 0 0 0!important;padding:0!important;accent-color:#4b7381!important;opacity:1!important;visibility:visible!important;box-shadow:none!important;background:none!important;transform:none!important;}',
        '.sm24-terms-row input.sm24-terms-cb::before,.sm24-terms-row input.sm24-terms-cb::after{content:none!important;display:none!important;}',
        '.sm24-terms-row .sm24-terms-label>span{display:inline!important;visibility:visible!important;opacity:1!important;color:#45474d!important;font-size:13.5px!important;line-height:1.5!important;flex:1 1 auto!important;white-space:normal!important;}',
        '.sm24-terms-row .sm24-terms-label>span a{color:#5b8b9c!important;font-weight:700!important;text-decoration:underline!important;}',
        '.sm24-terms-row .sm24-terms-hint{padding-left:27px!important;}'
      ].join('');
      host.appendChild(st);
    } catch (e) {}
  }

  function sm24InjectTermsInRoot(root) {
    if (!root || !root.querySelectorAll) return;

    // Skip the dashboard / logged-in views entirely.
    var holder = root.querySelector('#ae-holder');
    if (holder && holder.getAttribute('isloggedin') === 'true') return;
    sm24EnsureTermsStyle(root);

    var lang = detectLang();
    var t = (lang === 'en') ? {
      // "Terms & Conditions" is the linked text; rest is plain.
      pre: 'I agree to the ',
      link: 'Terms & Conditions',
      hint: 'Please accept the Terms & Conditions.'
    } : {
      pre: 'Ich stimme den ',
      link: 'AGB',
      post: ' zu',
      hint: 'Bitte stimmen Sie den AGB zu.'
    };

    var forms = root.querySelectorAll('form');
    forms.forEach(function (form) {
      if (!sm24IsSignupForm(form)) return;

      var submit = sm24FindSubmitButton(form);
      if (!submit) return;

      // Idempotency: one consent row per signup form.
      if (form.__sm24TermsInjected && form.querySelector('.sm24-terms-row')) {
        // Already injected — just keep the lang-dependent text fresh in case
        // of a language switch, and re-sync the disabled state.
        sm24SyncTermsState(form);
        return;
      }
      // Defensive: if a stale wrapper somehow exists, remove it before re-adding.
      var stale = form.querySelector('.sm24-terms-row');
      if (stale) stale.remove();

      var row = document.createElement('div');
      row.className = 'sm24-terms-row';
      row.style.cssText = 'display:flex;flex-direction:column;gap:6px;margin:14px 0 10px;font-family:Inter,-apple-system,sans-serif;';

      var line = document.createElement('label');
      line.className = 'sm24-terms-label';
      line.style.cssText = 'display:flex;align-items:flex-start;gap:9px;font-size:13.5px;line-height:1.5;color:#45474d;cursor:pointer;';

      var cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.className = 'sm24-terms-cb';
      // Intentionally NOT named — must not be submitted as a form field.
      cb.setAttribute('aria-describedby', 'sm24-terms-hint');
      cb.style.cssText = 'flex:0 0 auto;width:18px;height:18px;margin-top:1px;accent-color:#4b7381;cursor:pointer;';

      // Bilingual label text with the linked Terms page. Wrapped in
      // wg-notranslate so Weglot/translation doesn't mangle it; we ship
      // BOTH the DE and EN copy ourselves via detectLang().
      var textSpan = document.createElement('span');
      textSpan.className = 'wg-notranslate';
      textSpan.setAttribute('translate', 'no');
      // SM24_CHUB_TERMS_v1 fix 2026-06-07: the BUYER signup must link to the BUYER T&C
      // (/pages/t-c-b2b-customers = "AGB - B2B Kunden"), NOT the supplier terms
      // (/policies/terms-of-service = "AGB - Lieferanten"). Locale-aware href.
      var sm24TermsHref = (lang === 'en') ? '/en/pages/t-c-b2b-customers' : '/pages/t-c-b2b-customers';
      var anchor = '<a href="' + sm24TermsHref + '" target="_blank" rel="noopener" '
        + 'style="color:#5b8b9c;font-weight:700;text-decoration:underline;">' + t.link + '</a>';
      if (lang === 'en') {
        textSpan.innerHTML = t.pre + anchor;
      } else {
        textSpan.innerHTML = t.pre + anchor + t.post;
      }

      line.appendChild(cb);
      line.appendChild(textSpan);

      var hint = document.createElement('div');
      hint.className = 'sm24-terms-hint';
      hint.id = 'sm24-terms-hint';
      hint.setAttribute('role', 'alert');
      hint.textContent = t.hint;
      hint.style.cssText = 'display:none;font-size:12.5px;font-weight:600;color:#b91c1c;padding-left:27px;';

      row.appendChild(line);
      row.appendChild(hint);

      // Insert directly ABOVE the submit button. If the submit is wrapped in a
      // button-group container (.b-gr) that is itself inside the form, insert
      // before that wrapper so the consent row sits just above the visible
      // button rather than inside the button group.
      var insertBeforeNode = submit;
      var wrap = submit.closest ? submit.closest('.b-gr') : null;
      if (wrap && wrap !== submit && form.contains(wrap) && wrap.parentNode) {
        insertBeforeNode = wrap;
      }
      (insertBeforeNode.parentNode || form).insertBefore(row, insertBeforeNode);

      // Tag references for the sync/intercept helpers.
      form.__sm24TermsInjected = true;
      form.__sm24TermsCb = cb;
      form.__sm24TermsHint = hint;
      form.__sm24TermsSubmit = submit;

      // Checkbox change → clear hint + re-sync disabled state.
      cb.addEventListener('change', function () {
        if (cb.checked && hint) hint.style.display = 'none';
        sm24SyncTermsState(form);
      });

      // Capture-phase guards on the form: block submit while unchecked.
      // This is the authoritative gate (works even if C:Hub re-enables the
      // visually-disabled button). It does NOT touch the form when checked.
      if (!form.__sm24TermsSubmitGuard) {
        form.__sm24TermsSubmitGuard = true;
        form.addEventListener('submit', function (e) {
          var box = form.__sm24TermsCb;
          if (box && !box.checked) {
            e.preventDefault();
            e.stopImmediatePropagation();
            sm24ShowTermsHint(form);
            return false;
          }
        }, true);
      }

      sm24SyncTermsState(form);
    });
  }

  function sm24ShowTermsHint(form) {
    var box = form.__sm24TermsCb;
    var hint = form.__sm24TermsHint;
    if (hint) hint.style.display = 'block';
    if (box) {
      box.style.outline = '2px solid #dc2626';
      box.style.outlineOffset = '2px';
      try { box.focus(); } catch (e) {}
      setTimeout(function () { try { box.style.outline = ''; box.style.outlineOffset = ''; } catch (e) {} }, 2500);
    }
  }

  function sm24SyncTermsState(form) {
    var box = form.__sm24TermsCb;
    var submit = form.__sm24TermsSubmit;
    if (!box || !submit) return;
    var checked = !!box.checked;
    // Visual disabled state on the submit. We mark it but rely on the
    // capture-phase submit guard as the real gate (some C:Hub buttons are
    // <a>/<div> that ignore the `disabled` property).
    if (checked) {
      submit.removeAttribute('aria-disabled');
      submit.style.opacity = '';
      submit.style.pointerEvents = '';
      submit.style.cursor = '';
      if ('disabled' in submit) { try { submit.disabled = false; } catch (e) {} }
    } else {
      submit.setAttribute('aria-disabled', 'true');
      submit.style.opacity = '0.55';
      submit.style.cursor = 'not-allowed';
      // Do NOT set pointer-events:none — we need the click to reach our
      // capture-phase guard so we can show the hint. The guard blocks it.
      if ('disabled' in submit) { try { submit.disabled = false; } catch (e) {} }
    }

    // Capture-phase click guard on the submit itself (covers <a>/<div>
    // buttons that submit via JS rather than firing a native form submit).
    if (!submit.__sm24TermsClickGuard) {
      submit.__sm24TermsClickGuard = true;
      submit.addEventListener('click', function (e) {
        var b = form.__sm24TermsCb;
        if (b && !b.checked) {
          e.preventDefault();
          e.stopImmediatePropagation();
          sm24ShowTermsHint(form);
          return false;
        }
      }, true);
    }
  }

  function sm24InjectTermsEverywhere() {
    document.querySelectorAll('accounts-everywhere, accounts-hub').forEach(function (w) {
      if (w.shadowRoot) sm24InjectTermsInRoot(w.shadowRoot);
    });
  }
  /* ═══════════════════ END SM24_CHUB_TERMS_v1 ═══════════════════ */

  function applyAll() {
    styleAccountsEverywhere();
    translateAccountsEverywhere();
    populateHubMenuLabels();
    overrideForgotPasswordEverywhere();
    sm24InjectTermsEverywhere(); // SM24_CHUB_TERMS_v1
  }

  // Debounced applyAll to avoid running 100x per second under heavy DOM mutation
  var pending = null;
  function applyAllDebounced() {
    if (pending) return;
    pending = requestAnimationFrame(function () {
      pending = null;
      applyAll();
    });
  }

  function init() {
    injectTopLevelFabHide();
    applyAll();

    // Watch the widget for any DOM changes (page changes within widget, language switch, etc.)
    var bodyObserver = new MutationObserver(applyAllDebounced);
    bodyObserver.observe(document.body, { childList: true, subtree: true });

    // Watch <html lang> attribute for Weglot language changes
    var htmlObserver = new MutationObserver(applyAll);
    htmlObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] });

    // Listen for Weglot's languageChanged event if Weglot is loaded
    if (window.Weglot && typeof window.Weglot.on === 'function') {
      try { window.Weglot.on('languageChanged', applyAll); } catch (e) {}
    }

    // C:Hub uses URL hash for internal routing (#account:a:register, #account:a:login).
    // Re-translate when hash changes so newly-rendered views (welcome, login, signup) get translated.
    window.addEventListener('hashchange', function () {
      // small delay to let C:Hub finish rendering the new view
      setTimeout(applyAll, 50);
      setTimeout(applyAll, 300);
    });
    window.addEventListener('popstate', function () {
      setTimeout(applyAll, 50);
      setTimeout(applyAll, 300);
    });

    // Watch each <accounts-everywhere> AND <accounts-hub> shadow root directly when present
    function attachShadowObservers() {
      document.querySelectorAll('accounts-everywhere, accounts-hub').forEach(function (w) {
        if (w.shadowRoot && !w.__sm24Observed) {
          w.__sm24Observed = true;
          new MutationObserver(applyAllDebounced).observe(w.shadowRoot, { childList: true, subtree: true, characterData: true });
        }
      });
    }
    attachShadowObservers();
    setTimeout(attachShadowObservers, 500);
    setTimeout(attachShadowObservers, 2000);

    // Aggressive safety retries (covers slow-rendering shadow DOM updates)
    [100, 300, 600, 1200, 2500, 5000, 10000].forEach(function (delay) {
      setTimeout(applyAll, delay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
